import React, { useState, useEffect } from 'react';
import { FaStar, FaCheckCircle, FaPlus, FaTimes, FaSpinner, FaQuoteLeft, FaAward, FaUsers, FaThumbsUp } from 'react-icons/fa';
import apiService from '../services/api';

const ReviewsPage = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        rating: 5,
        review: ''
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const data = await apiService.getPublicReviews();
            setReviews(data);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            setError('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRatingClick = (rating) => {
        setFormData(prev => ({ ...prev, rating }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }

        if (formData.review.trim().length < 10) {
            setError('Review must be at least 10 characters long');
            return;
        }

        try {
            setSubmitting(true);
            setError('');

            await apiService.submitReview({
                name: formData.name.trim(),
                email: formData.email.trim() || null,
                rating: formData.rating,
                review: formData.review.trim()
            });

            setMessage('Thank you! Your review has been submitted and is pending approval.');
            setFormData({ name: '', email: '', rating: 5, review: '' });
            setShowAddForm(false);

            // Refresh reviews
            setTimeout(() => {
                fetchReviews();
                setMessage('');
            }, 3000);

        } catch (error) {
            console.error('Error submitting review:', error);
            setError(error.message || 'Failed to submit review. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStars = (rating, interactive = false, onRatingClick = null) => {
        return [...Array(5)].map((_, index) => (
            <FaStar
                key={index}
                className={`star ${index < rating ? 'star-filled' : 'star-empty'} ${interactive ? 'interactive' : ''}`}
                onClick={() => interactive && onRatingClick && onRatingClick(index + 1)}
            />
        ));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getAverageRating = () => {
        if (reviews.length === 0) return 0;
        const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
        return (sum / reviews.length).toFixed(1);
    };

    const getRatingDistribution = () => {
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviews.forEach(review => {
            distribution[review.rating]++;
        });
        return distribution;
    };

    if (loading) {
        return (
            <div className="reviews-page">
                <div className="container">
                    <div className="loading-container">
                        <div className="loading-spinner">
                            <FaSpinner className="spinner" />
                        </div>
                        <h3>Loading reviews...</h3>
                        <p>Please wait while we fetch the latest customer feedback</p>
                    </div>
                </div>
            </div>
        );
    }

    const avgRating = getAverageRating();
    const ratingDistribution = getRatingDistribution();
    const verifiedReviews = reviews.filter(r => r.is_verified).length;

    return (
        <div className="reviews-page">
            {/* Hero Section */}
            <div className="hero-section">
                <div className="container">
                    <div className="hero-content">
                        <h1>Customer Reviews & Testimonials</h1>
                        <p className="hero-subtitle">
                            Discover why thousands of teams trust TaskPriority to streamline their workflow
                        </p>

                        {/* Review Stats */}
                        <div className="review-stats">
                            <div className="stat-card primary">
                                <div className="stat-icon">
                                    <FaStar />
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{avgRating}</div>
                                    <div className="stat-label">Average Rating</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">
                                    <FaUsers />
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{reviews.length}</div>
                                    <div className="stat-label">Total Reviews</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">
                                    <FaAward />
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{verifiedReviews}</div>
                                    <div className="stat-label">Verified Reviews</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">
                                    <FaThumbsUp />
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{Math.round((ratingDistribution[4] + ratingDistribution[5]) / reviews.length * 100) || 0}%</div>
                                    <div className="stat-label">Satisfaction Rate</div>
                                </div>
                            </div>
                        </div>

                        <button
                            className="btn btn-primary btn-hero"
                            onClick={() => setShowAddForm(true)}
                        >
                            <FaPlus />
                            Share Your Experience
                        </button>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Messages */}
                {message && (
                    <div className="alert alert-success">
                        <FaCheckCircle />
                        <div>
                            <strong>Success!</strong>
                            <p>{message}</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        <div>
                            <strong>Error</strong>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {/* Rating Distribution */}
                {reviews.length > 0 && (
                    <div className="rating-breakdown">
                        <h2>Rating Distribution</h2>
                        <div className="rating-bars">
                            {[5, 4, 3, 2, 1].map(rating => (
                                <div key={rating} className="rating-bar-row">
                                    <div className="rating-label">
                                        <span>{rating}</span>
                                        <FaStar className="star-small" />
                                    </div>
                                    <div className="rating-bar">
                                        <div
                                            className="rating-bar-fill"
                                            style={{
                                                width: `${reviews.length > 0 ? (ratingDistribution[rating] / reviews.length) * 100 : 0}%`
                                            }}
                                        ></div>
                                    </div>
                                    <div className="rating-count">
                                        {ratingDistribution[rating]}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Review Modal */}
                {showAddForm && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div>
                                    <h2>Share Your Experience</h2>
                                    <p>Help others discover TaskPriority</p>
                                </div>
                                <button
                                    className="close-btn"
                                    onClick={() => setShowAddForm(false)}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="review-form">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="name">
                                            Your Name <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="email">
                                            Email <span className="optional">(optional)</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>
                                        Your Rating <span className="required">*</span>
                                    </label>
                                    <div className="rating-input">
                                        <div className="stars-container">
                                            {renderStars(formData.rating, true, handleRatingClick)}
                                        </div>
                                        <div className="rating-feedback">
                                            <span className="rating-text">
                                                {formData.rating === 5 ? '⭐ Excellent!' :
                                                    formData.rating === 4 ? '👍 Very Good' :
                                                        formData.rating === 3 ? '👌 Good' :
                                                            formData.rating === 2 ? '👎 Fair' :
                                                                '😞 Needs Improvement'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="review">
                                        Your Review <span className="required">*</span>
                                    </label>
                                    <textarea
                                        id="review"
                                        name="review"
                                        value={formData.review}
                                        onChange={handleInputChange}
                                        required
                                        rows="5"
                                        placeholder="Share your experience with TaskPriority. What do you like most? How has it helped your workflow?"
                                        minLength="10"
                                    />
                                    <div className="form-footer">
                                        <small className="character-count">
                                            {formData.review.length}/10 characters minimum
                                        </small>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowAddForm(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <FaSpinner className="spinner" />
                                                Publishing...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheckCircle />
                                                Publish Review
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Reviews Grid */}
                <div className="reviews-section">
                    <div className="section-header">
                        <h2>What Our Customers Say</h2>
                        <p>Real feedback from teams using TaskPriority to enhance their productivity</p>
                    </div>

                    <div className="reviews-grid">
                        {reviews.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <FaQuoteLeft />
                                </div>
                                <h3>Be the First to Review!</h3>
                                <p>Share your experience and help others discover the benefits of TaskPriority</p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowAddForm(true)}
                                >
                                    <FaPlus />
                                    Write the First Review
                                </button>
                            </div>
                        ) : (
                            reviews.map((review, index) => (
                                <div key={review.id} className={`review-card ${index < 3 ? 'featured' : ''}`}>
                                    <div className="review-header">
                                        <div className="reviewer-info">
                                            <div className="reviewer-avatar">
                                                {review.name.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div className="reviewer-details">
                                                <h4 className="reviewer-name">{review.name}</h4>
                                                <div className="review-meta">
                                                    <div className="review-stars">
                                                        {renderStars(review.rating)}
                                                    </div>
                                                    <span className="review-date">{formatDate(review.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {review.is_verified && (
                                            <div className="verified-badge">
                                                <FaCheckCircle />
                                                <span>Verified</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="review-content">
                                        <FaQuoteLeft className="quote-icon" />
                                        <p className="review-text">{review.review}</p>
                                    </div>

                                    {index < 3 && (
                                        <div className="featured-badge">
                                            <FaAward />
                                            Featured Review
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {reviews.length > 0 && (
                        <div className="reviews-footer">
                            <div className="testimonial-cta">
                                <h3>Join Our Community</h3>
                                <p>Share your TaskPriority success story</p>
                                <button
                                    className="btn btn-outline btn-large"
                                    onClick={() => setShowAddForm(true)}
                                >
                                    <FaPlus />
                                    Add Your Review
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .reviews-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    position: relative;
                }

                .reviews-page::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 100%;
                    background: linear-gradient(135deg, 
                        rgba(102, 126, 234, 0.9) 0%, 
                        rgba(118, 75, 162, 0.9) 100%);
                    z-index: 1;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 24px;
                    position: relative;
                    z-index: 2;
                }

                /* Hero Section */
                .hero-section {
                    padding: 80px 0;
                    text-align: center;
                    position: relative;
                    z-index: 2;
                }

                .hero-content h1 {
                    font-size: 56px;
                    font-weight: 800;
                    color: white;
                    margin-bottom: 24px;
                    letter-spacing: -0.025em;
                    text-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .hero-subtitle {
                    font-size: 22px;
                    color: rgba(255, 255, 255, 0.9);
                    margin-bottom: 48px;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    line-height: 1.6;
                }

                .review-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 24px;
                    margin-bottom: 48px;
                    max-width: 800px;
                    margin-left: auto;
                    margin-right: auto;
                }

                .stat-card {
                    background: rgba(255, 255, 255, 0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    border-radius: 16px;
                    padding: 24px;
                    text-align: center;
                    transition: all 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    background: rgba(255, 255, 255, 0.15);
                }

                .stat-card.primary {
                    background: rgba(255, 255, 255, 0.2);
                    border: 2px solid rgba(255, 255, 255, 0.3);
                }

                .stat-icon {
                    width: 48px;
                    height: 48px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                    font-size: 20px;
                    color: white;
                }

                .stat-number {
                    font-size: 32px;
                    font-weight: 700;
                    color: white;
                    margin-bottom: 4px;
                }

                .stat-label {
                    font-size: 14px;
                    color: rgba(255, 255, 255, 0.8);
                    font-weight: 500;
                }

                .btn-hero {
                    padding: 16px 32px;
                    font-size: 18px;
                    font-weight: 600;
                    background: white;
                    color: #667eea;
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;
                }

                .btn-hero:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
                    background: #f8fafc;
                }

                /* Loading */
                .loading-container {
                    text-align: center;
                    padding: 120px 0;
                }

                .loading-spinner {
                    margin-bottom: 24px;
                }

                .spinner {
                    animation: spin 1s linear infinite;
                    font-size: 32px;
                    color: white;
                }

                .loading-container h3 {
                    font-size: 24px;
                    font-weight: 600;
                    color: white;
                    margin-bottom: 12px;
                }

                .loading-container p {
                    color: rgba(255, 255, 255, 0.8);
                    font-size: 16px;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Content Sections */
                .reviews-section {
                    background: #f8fafc;
                    border-radius: 24px 24px 0 0;
                    padding: 80px 0;
                    margin-top: 40px;
                    position: relative;
                    z-index: 3;
                }

                .section-header {
                    text-align: center;
                    margin-bottom: 64px;
                }

                .section-header h2 {
                    font-size: 40px;
                    font-weight: 800;
                    color: #1a202c;
                    margin-bottom: 16px;
                }

                .section-header p {
                    font-size: 18px;
                    color: #64748b;
                    max-width: 600px;
                    margin: 0 auto;
                }

                /* Rating Breakdown */
                .rating-breakdown {
                    background: white;
                    border-radius: 16px;
                    padding: 40px;
                    margin: 40px 0;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                }

                .rating-breakdown h2 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 24px;
                    text-align: center;
                }

                .rating-bars {
                    max-width: 400px;
                    margin: 0 auto;
                }

                .rating-bar-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 12px;
                }

                .rating-label {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    min-width: 40px;
                    font-weight: 600;
                    color: #374151;
                }

                .star-small {
                    font-size: 12px;
                    color: #f59e0b;
                }

                .rating-bar {
                    flex: 1;
                    height: 8px;
                    background: #e5e7eb;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .rating-bar-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #f59e0b, #d97706);
                    transition: width 0.5s ease;
                }

                .rating-count {
                    min-width: 30px;
                    text-align: right;
                    font-weight: 600;
                    color: #64748b;
                }

                /* Alerts */
                .alert {
                    padding: 20px 24px;
                    border-radius: 12px;
                    margin: 32px 0;
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    font-weight: 500;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                }

                .alert-success {
                    background: white;
                    color: #065f46;
                    border-left: 4px solid #10b981;
                }

                .alert-error {
                    background: white;
                    color: #991b1b;
                    border-left: 4px solid #ef4444;
                }

                .alert strong {
                    display: block;
                    margin-bottom: 4px;
                }

                .alert p {
                    margin: 0;
                    font-weight: 400;
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    backdrop-filter: blur(4px);
                }

                .modal-content {
                    background: white;
                    border-radius: 20px;
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    border: 1px solid #e5e7eb;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 32px 32px 0 32px;
                    margin-bottom: 32px;
                }

                .modal-header h2 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0 0 8px 0;
                }

                .modal-header p {
                    color: #64748b;
                    margin: 0;
                    font-size: 16px;
                }

                .close-btn {
                    background: #f1f5f9;
                    border: none;
                    font-size: 20px;
                    color: #64748b;
                    cursor: pointer;
                    padding: 12px;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    margin-left: 16px;
                }

                .close-btn:hover {
                    background: #e2e8f0;
                    color: #1a202c;
                }

                .review-form {
                    padding: 0 32px 32px 32px;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-bottom: 24px;
                }

                .form-group {
                    margin-bottom: 24px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #374151;
                    font-size: 15px;
                }

                .required {
                    color: #ef4444;
                }

                .optional {
                    color: #64748b;
                    font-weight: 400;
                    font-size: 14px;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    font-family: inherit;
                    background: #fafafa;
                }

                .form-group input:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                    background: white;
                }

                .rating-input {
                    margin-top: 12px;
                }

                .stars-container {
                    margin-bottom: 12px;
                }

                .star {
                    font-size: 24px;
                    margin-right: 4px;
                    transition: all 0.2s ease;
                }

                .star-filled {
                    color: #f59e0b;
                }

                .star-empty {
                    color: #d1d5db;
                }

                .star.interactive {
                    cursor: pointer;
                    font-size: 28px;
                }

                .star.interactive:hover {
                    transform: scale(1.1);
                    filter: brightness(1.1);
                }

                .rating-feedback {
                    text-align: center;
                }

                .rating-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: #374151;
                    padding: 8px 16px;
                    background: #f1f5f9;
                    border-radius: 20px;
                    display: inline-block;
                }

                .form-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 8px;
                }

                .character-count {
                    font-size: 12px;
                    color: #64748b;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 16px;
                    margin-top: 40px;
                    padding-top: 24px;
                    border-top: 1px solid #e5e7eb;
                }

                .btn {
                    padding: 12px 24px;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 15px;
                    border: none;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                    text-decoration: none;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);
                }

                .btn-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.35);
                }

                .btn-secondary {
                    background: #f8fafc;
                    color: #64748b;
                    border: 2px solid #e2e8f0;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #374151;
                    border-color: #cbd5e0;
                }

                .btn-outline {
                    background: transparent;
                    color: #667eea;
                    border: 2px solid #667eea;
                }

                .btn-outline:hover {
                    background: #667eea;
                    color: white;
                }

                .btn-large {
                    padding: 16px 32px;
                    font-size: 16px;
                }

                /* Reviews Grid */
                .reviews-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
                    gap: 32px;
                    margin-bottom: 64px;
                }

                .empty-state {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 80px 40px;
                    background: white;
                    border-radius: 20px;
                    border: 2px dashed #cbd5e0;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                }

                .empty-icon {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 24px;
                    color: white;
                    font-size: 32px;
                }

                .empty-state h3 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 12px;
                }

                .empty-state p {
                    font-size: 16px;
                    color: #64748b;
                    margin-bottom: 32px;
                    max-width: 400px;
                    margin-left: auto;
                    margin-right: auto;
                }

                .review-card {
                    background: white;
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }

                .review-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                .review-card.featured {
                    border: 2px solid #f59e0b;
                    background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
                }

                .review-card.featured:hover {
                    box-shadow: 0 20px 25px rgba(245, 158, 11, 0.2);
                }

                .review-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 24px;
                }

                .reviewer-info {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .reviewer-avatar {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 700;
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .reviewer-details {
                    flex: 1;
                }

                .reviewer-name {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0 0 8px 0;
                }

                .review-meta {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .review-stars {
                    display: flex;
                    gap: 2px;
                }

                .review-stars .star {
                    font-size: 16px;
                }

                .review-date {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                }

                .verified-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #d1fae5;
                    color: #065f46;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    flex-shrink: 0;
                }

                .verified-badge svg {
                    font-size: 12px;
                }

                .review-content {
                    position: relative;
                }

                .quote-icon {
                    position: absolute;
                    top: -8px;
                    left: -8px;
                    font-size: 24px;
                    color: #cbd5e0;
                    z-index: 0;
                }

                .review-text {
                    font-size: 16px;
                    line-height: 1.7;
                    color: #374151;
                    margin: 0;
                    position: relative;
                    z-index: 1;
                    font-style: italic;
                }

                .featured-badge {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    background: #f59e0b;
                    color: white;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .featured-badge svg {
                    font-size: 10px;
                }

                /* Reviews Footer */
                .reviews-footer {
                    text-align: center;
                    padding: 64px 0;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-radius: 20px;
                    margin-top: 40px;
                }

                .testimonial-cta h3 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 12px;
                }

                .testimonial-cta p {
                    font-size: 18px;
                    color: #64748b;
                    margin-bottom: 32px;
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .container {
                        padding: 0 16px;
                    }

                    .hero-content h1 {
                        font-size: 40px;
                    }

                    .hero-subtitle {
                        font-size: 18px;
                    }

                    .review-stats {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 16px;
                    }

                    .reviews-grid {
                        grid-template-columns: 1fr;
                        gap: 24px;
                    }

                    .modal-content {
                        margin: 16px;
                        max-height: calc(100vh - 32px);
                        border-radius: 16px;
                    }

                    .modal-header,
                    .review-form {
                        padding-left: 24px;
                        padding-right: 24px;
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .form-actions {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .rating-breakdown {
                        padding: 24px;
                    }

                    .section-header h2 {
                        font-size: 32px;
                    }

                    .testimonial-cta h3 {
                        font-size: 24px;
                    }

                    .review-card {
                        padding: 24px;
                    }

                    .reviewer-info {
                        gap: 12px;
                    }

                    .reviewer-avatar {
                        width: 48px;
                        height: 48px;
                        font-size: 16px;
                    }
                }

                @media (max-width: 480px) {
                    .hero-content h1 {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .review-stats {
                        grid-template-columns: 1fr;
                    }

                    .stat-card {
                        padding: 20px;
                    }

                    .stat-number {
                        font-size: 28px;
                    }

                    .reviews-section {
                        padding: 60px 0;
                    }

                    .section-header {
                        margin-bottom: 48px;
                    }

                    .empty-state {
                        padding: 60px 24px;
                    }

                    .empty-icon {
                        width: 64px;
                        height: 64px;
                        font-size: 24px;
                    }

                    .review-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                    }

                    .verified-badge {
                        align-self: flex-start;
                    }

                    .rating-bars {
                        max-width: none;
                    }

                    .rating-bar-row {
                        gap: 12px;
                    }

                    .rating-label {
                        min-width: 35px;
                    }

                    .rating-count {
                        min-width: 25px;
                    }
                }

                /* Animation Enhancements */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .review-card {
                    animation: fadeInUp 0.6s ease-out;
                }

                .review-card:nth-child(1) { animation-delay: 0.1s; }
                .review-card:nth-child(2) { animation-delay: 0.2s; }
                .review-card:nth-child(3) { animation-delay: 0.3s; }
                .review-card:nth-child(4) { animation-delay: 0.4s; }
                .review-card:nth-child(5) { animation-delay: 0.5s; }
                .review-card:nth-child(6) { animation-delay: 0.6s; }

                /* Accessibility Improvements */
                @media (prefers-reduced-motion: reduce) {
                    * {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }

                .btn:focus-visible,
                .close-btn:focus-visible {
                    outline: 2px solid #667eea;
                    outline-offset: 2px;
                }

                .form-group input:focus-visible,
                .form-group textarea:focus-visible {
                    outline: 2px solid #667eea;
                    outline-offset: -2px;
                }
            `}</style>
        </div>
    );
};

export default ReviewsPage;